/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type AnswerNowStatus,
	AnswerNowStatusType,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { CompactResearchSteps } from '@atlassian/rovo-platform-ui-components/compact-research-steps';
import { MessageLoading } from '@atlassian/rovo-platform-ui-components/loading-state/MessageLoading';
import { ConnectedSkillTag } from '@atlassian/rovo-skills/ui/connected-skill-tag';

import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { useXpcLinkWrapper } from '../../../common/utils/use-xpc-link-wrapper';
import { useChatMessageHasThinkDeeperTriggered } from '../../../controllers/chat-message-research-store/useChatMessageHasThinkDeeperTriggered';
import { useChatMessageResearchByRoundId } from '../../../controllers/chat-message-research-store/useChatMessageResearchByRoundId';
import { useRoundedResearch } from '../../../controllers/chat-message-research-store/useRoundedResearch';
import { useConversation } from '../../../controllers/conversation/useConversation';
import { AnswerNowButton } from '../../answer-now-button';
import { ViewDeadAirTracker } from '../../view-dead-air-tracker/ViewDeadAirTracker';
import { ViewDeadAirZone } from '../../view-dead-air-tracker/ViewDeadAirZone';
import chatHistoryMessages from '../messages';

import { MessageLoadingPaddedContainer } from './MessageLoadingPaddedContainer';
import messages from './messages';

const roundSeparatorStyles = cssMap({
	root: {
		marginBlockEnd: token('space.200'),
	},
});

const ThinkDeeperResearchRoundCompact = ({
	roundId,
	messageId,
	hasStreamedContent,
	isLastRound,
	generatingText,
	personalityThinkingText,
	answerNowSlot,
	hideLiveThinkingIndicator,
}: {
	roundId: number;
	messageId: string;
	hasStreamedContent: boolean;
	isLastRound: boolean;
	generatingText?: string;
	personalityThinkingText?: string;
	answerNowSlot?: React.ReactNode;
	hideLiveThinkingIndicator?: boolean;
}) => {
	const { artificiallyProgressingSteps, errored, researchCompleted, isAllStepsCompleted } =
		useChatMessageResearchByRoundId({
			roundId,
			messageId,
		});
	const { formatMessage } = useIntl();
	const {
		productKey,
		siteId,
		profileData: { aaid },
	} = useGlobalConfig();
	const { customHandlers } = useUIConfig();
	const { conversation } = useConversation();
	const hasThinkDeeperTriggered = useChatMessageHasThinkDeeperTriggered({
		messageId: messageId ?? '',
	});
	const { sendAnalyticsEvent } = useAnalytics();
	const xpcWrapUrl = useXpcLinkWrapper();

	if (errored) {
		return null;
	}

	// Use isAllStepsCompleted from the hook — it returns true when steps is empty (length === 0),
	// preventing a flash where the thinking indicator re-appears during a momentary empty steps state
	// (e.g. between updateResearchMessageId and the new store entry being populated).
	const allArtificialStepsCompleted = isAllStepsCompleted;
	const isCompleted =
		researchCompleted || (allArtificialStepsCompleted && (hasStreamedContent || !isLastRound));

	const renderSubstep = (substep: string, isStreamed?: boolean) => {
		return (
			<ViewDeadAirZone
				id={`substep-${messageId}-${roundId}`}
				contentType={isStreamed ? 'stream' : 'discrete'}
			>
				<ContentRenderer
					content={substep}
					smallerFont={true}
					subtlestText={true}
					productKey={productKey}
					siteId={siteId}
					aaid={aaid}
					conversationId={conversation?.id}
					onDeepLinkClick={customHandlers?.handleDeepLinkClick}
					shouldOverrideCardAsInline={true}
				/>
			</ViewDeadAirZone>
		);
	};

	if (artificiallyProgressingSteps.length === 0) {
		// If the answer has already started streaming in, there are no traces to show
		// and we should not render the thinking indicator at all — this handles the case
		// where ANSWER_PART arrives before any RESEARCH_STEP_TITLE/trace.
		if (hasStreamedContent) {
			return null;
		}
		// If this is not the last round, a later round already has steps. This empty round
		// is just an implicit plan header — do not render a thinking indicator for it,
		// as that would duplicate the global indicator from renderTracesAndErrors.
		if (!isLastRound) {
			return null;
		}
		// hideLiveThinkingIndicator is set when the answer has started streaming (hasReceivedAnswerPart)
		// or when streamingComplete is true. Guard here to avoid a flash where steps have emptied
		// but hasStreamedContent hasn't updated yet.
		if (!allArtificialStepsCompleted && !hideLiveThinkingIndicator) {
			// Use the hook value instead of manually iterating traces
			const thinkingText =
				generatingText ??
				personalityThinkingText ??
				(hasThinkDeeperTriggered
					? formatMessage(chatHistoryMessages.thinkDeeperThinking)
					: formatMessage(messages.rovoIsThinking));

			return (
				<MessageLoading
					traces={[]}
					hideThinkingIndicator={false}
					thinkingText={thinkingText}
					animation={true}
				/>
			);
		}
		return null;
	}

	const artificiallyProgressingStepsWithSkills = artificiallyProgressingSteps.map((step) => {
		if (step.isSkill) {
			return {
				...step,
				title: (
					<ConnectedSkillTag slug={step.title} shimmer={!step.completed} siteId={siteId ?? ''} />
				),
			};
		}
		return step;
	});

	// Use the hook value instead of manually iterating traces
	return (
		<ViewDeadAirTracker id={`research-steps-${messageId}-${roundId}`} contentType="discrete">
			<CompactResearchSteps
				steps={artificiallyProgressingStepsWithSkills}
				researchCompleted={isCompleted}
				responseStreaming={hasStreamedContent}
				renderSubstep={renderSubstep}
				thinkDeeperTriggered={hasThinkDeeperTriggered}
				answerNowSlot={answerNowSlot}
				xpcWrapUrl={xpcWrapUrl}
				onTraceInteraction={({ type, expanded }) => {
					sendAnalyticsEvent({
						eventType: 'ui',
						action: expanded ? 'expanded' : 'collapsed',
						actionSubject: 'trace',
						actionSubjectId: type,
						attributes: {
							messageId,
							roundId,
						},
					});
				}}
			/>
		</ViewDeadAirTracker>
	);
};

const ThinkDeeperResearchRound = ({
	roundId,
	messageId,
	hasStreamedContent,
	isLastRound,
	generatingText,
	personalityThinkingText,
	answerNowStatus,
	onAnswerNowClick,
	hideLiveThinkingIndicator,
}: {
	roundId: number;
	messageId: string;
	hasStreamedContent: boolean;
	isLastRound: boolean;
	generatingText?: string;
	personalityThinkingText?: string;
	answerNowStatus?: AnswerNowStatus;
	onAnswerNowClick?: () => void;
	hideLiveThinkingIndicator?: boolean;
}) => {
	const showAnswerNow =
		isLastRound &&
		onAnswerNowClick &&
		(answerNowStatus?.type === AnswerNowStatusType.AVAILABLE ||
			answerNowStatus?.type === AnswerNowStatusType.SWITCHING);

	const answerNowButton = showAnswerNow ? (
		<AnswerNowButton
			onClick={onAnswerNowClick}
			isLoading={answerNowStatus?.type === AnswerNowStatusType.SWITCHING}
			isDisabled={answerNowStatus?.type === AnswerNowStatusType.SWITCHING}
			testId="think-deeper-answer-now-button"
			spacing="compact"
		/>
	) : undefined;

	return (
		<ThinkDeeperResearchRoundCompact
			roundId={roundId}
			messageId={messageId}
			hasStreamedContent={hasStreamedContent}
			isLastRound={isLastRound}
			generatingText={generatingText}
			personalityThinkingText={personalityThinkingText}
			answerNowSlot={answerNowButton}
			hideLiveThinkingIndicator={hideLiveThinkingIndicator}
		/>
	);
};

export const MessageThinkDeeperResearch = ({
	messageId,
	streamedContent,
	generatingText,
	personalityThinkingText,
	answerNowStatus,
	onAnswerNowClick,
	hideLiveThinkingIndicator,
}: {
	messageId: string;
	streamedContent: string;
	generatingText?: string;
	personalityThinkingText?: string;
	answerNowStatus?: AnswerNowStatus;
	onAnswerNowClick?: () => void;
	hideLiveThinkingIndicator?: boolean;
}): JSX.Element[] | undefined => {
	const { rounds, maxRoundId } = useRoundedResearch({ messageId });
	const {
		productKey,
		siteId,
		profileData: { aaid },
	} = useGlobalConfig();

	if (rounds.length === 0) {
		return undefined;
	}

	return rounds.map(({ roundId, content }) => {
		// Prevents duplicate research plans in chat history during concurrent store updates
		// Chat history uses ResearchStore and MessagesStore which update asynchronously
		// During RESEARCH_PLAN flow:
		// 1. New plan created in ResearchStore
		// 2. Streamed content cleared from MessagesStore
		// 3. Plan rendered as regular message for proper ordering
		// React 18 concurrency can cause ResearchMessage to render before stream clears
		// This check prevents showing duplicate plans by comparing content equality
		// Misc other hackes were attempted, without using a single store: flushSync, component re-structuring
		const isResearchPlanContentEqualToStreamedContent = content === streamedContent;
		const isLastRound = roundId === maxRoundId;
		return (
			<React.Fragment key={`think-deeper-research-round-${roundId}`}>
				<ThinkDeeperResearchRound
					roundId={roundId}
					messageId={messageId}
					isLastRound={roundId === maxRoundId}
					hasStreamedContent={!!streamedContent}
					generatingText={generatingText}
					personalityThinkingText={personalityThinkingText}
					answerNowStatus={answerNowStatus}
					onAnswerNowClick={onAnswerNowClick}
					hideLiveThinkingIndicator={hideLiveThinkingIndicator}
				/>

				{content && !isResearchPlanContentEqualToStreamedContent && (
					<MessageLoadingPaddedContainer>
						<ViewDeadAirTracker id={`content-${messageId}-${roundId}`} contentType="stream">
							<ContentRenderer
								content={content}
								smallerFont={true}
								productKey={productKey}
								siteId={siteId}
								aaid={aaid}
								shouldOverrideCardAsInline={true}
							/>
						</ViewDeadAirTracker>
					</MessageLoadingPaddedContainer>
				)}
				{!isLastRound && rounds.length > 1 && <Box xcss={roundSeparatorStyles.root} />}
			</React.Fragment>
		);
	});
};
